import { useId, useRef, useState, type DragEvent } from 'react'
import { FileText, Image, Plus, Upload, X } from 'lucide-react'
import {
  dropshipV2ErrorMessage,
  type DropshipV2AIBuild,
  useUploadDropshipV2AIQuote,
} from '@/api/dropship-v2'
import { LoadingButton } from '@/components/loading-button'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

const MAX_QUOTE_FILE_BYTES = 20 * 1024 * 1024
const QUOTE_FILE_TYPES = new Set(['application/pdf', 'image/png', 'image/jpeg', 'image/webp'])
const QUOTE_FILE_ACCEPT = [...QUOTE_FILE_TYPES].join(',')

function quoteFileError(file: File) {
  if (!QUOTE_FILE_TYPES.has(file.type)) {
    return 'Choose a PDF, PNG, JPEG, or WebP file.'
  }
  if (file.size > MAX_QUOTE_FILE_BYTES) {
    return 'The file is larger than 20 MiB. Choose a smaller source file.'
  }
  return null
}

export function DropshipAIQuoteUpload({
  draftUuid,
  contentRevision,
  onBuildStarted,
  onManualAdd,
}: {
  draftUuid: string
  contentRevision: number
  onBuildStarted: (build: DropshipV2AIBuild) => void
  onManualAdd: () => void
}) {
  const inputId = useId()
  const inputRef = useRef<HTMLInputElement>(null)
  const upload = useUploadDropshipV2AIQuote(draftUuid)
  const [file, setFile] = useState<File | null>(null)
  const [dragActive, setDragActive] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const chooseFile = (nextFile?: File) => {
    if (!nextFile) return
    const validationError = quoteFileError(nextFile)
    setError(validationError)
    setFile(validationError ? null : nextFile)
  }

  const clearFile = () => {
    setFile(null)
    setError(null)
  }

  const handleDrop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault()
    setDragActive(false)
    if (event.dataTransfer.files.length !== 1) {
      setFile(null)
      setError('Choose one quote source file at a time.')
      return
    }
    chooseFile(event.dataTransfer.files[0])
  }

  const startBuild = () => {
    if (!file) return
    setError(null)
    upload.mutate(
      { file, expectedContentRevision: contentRevision },
      {
        onSuccess: (build) => {
          setFile(null)
          onBuildStarted(build)
        },
        onError: (uploadError) =>
          setError(dropshipV2ErrorMessage(uploadError, 'The quote could not be uploaded. Retry.')),
      },
    )
  }

  return (
    <div className="flex min-h-64 flex-col items-center justify-center px-4 py-8 text-center sm:px-6">
      <h3 className="font-semibold">No equipment lines yet</h3>
      <p className="mt-1 max-w-xl text-sm text-muted-foreground">
        Start from a marked-up quote or add equipment manually. PDF annotations and comments are
        read first, then every equipment row is matched in parallel.
      </p>

      <input
        ref={inputRef}
        id={inputId}
        type="file"
        accept={QUOTE_FILE_ACCEPT}
        tabIndex={-1}
        aria-label="Quote source file"
        className="sr-only"
        onChange={(event) => {
          chooseFile(event.target.files?.[0])
          event.target.value = ''
        }}
      />
      <button
        type="button"
        data-drag-active={dragActive ? 'true' : 'false'}
        aria-label="Choose quote PDF or image"
        className={cn(
          'mt-5 flex min-h-24 w-full max-w-xl items-center justify-center gap-3 rounded-lg border border-dashed px-4 py-4 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50',
          dragActive
            ? 'border-primary bg-primary/5'
            : 'border-border bg-muted/15 hover:border-muted-foreground/50 hover:bg-muted/30',
        )}
        onClick={() => inputRef.current?.click()}
        onDragEnter={(event) => {
          event.preventDefault()
          setDragActive(true)
        }}
        onDragOver={(event) => event.preventDefault()}
        onDragLeave={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget as Node | null))
            setDragActive(false)
        }}
        onDrop={handleDrop}
      >
        <span className="flex size-10 shrink-0 items-center justify-center rounded-md border bg-background">
          <Upload className="size-4 text-muted-foreground" aria-hidden="true" />
        </span>
        <span className="min-w-0">
          <span className="block text-sm font-medium">
            {dragActive ? 'Drop to attach this quote' : 'Drop a quote here or choose a file'}
          </span>
          <span className="mt-0.5 block text-xs text-muted-foreground">
            PDF, PNG, JPEG, or WebP, up to 20 MiB
          </span>
        </span>
      </button>

      {file ? (
        <div className="mt-3 flex min-h-11 w-full max-w-xl items-center gap-2 rounded-md border bg-background px-3 text-left text-sm">
          {file.type === 'application/pdf' ? (
            <FileText className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          ) : (
            <Image className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          )}
          <span className="min-w-0 flex-1 truncate">{file.name}</span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="size-11 shrink-0 sm:size-8"
            aria-label={`Remove ${file.name}`}
            onClick={clearFile}
          >
            <X className="size-4" />
          </Button>
        </div>
      ) : null}

      {error ? (
        <p role="alert" className="mt-3 max-w-xl text-sm text-destructive">
          {error}
        </p>
      ) : null}

      <div className="mt-4 flex w-full max-w-xl flex-col-reverse gap-2 sm:flex-row sm:justify-center">
        <Button
          type="button"
          variant="outline"
          className="min-h-11 sm:min-h-9"
          onClick={onManualAdd}
        >
          <Plus className="size-4" /> Enter equipment manually
        </Button>
        <LoadingButton
          type="button"
          className="min-h-11 sm:min-h-9"
          loading={upload.isPending}
          loadingText="Starting quote agent..."
          disabled={!file}
          onClick={startBuild}
        >
          <Upload className="size-4" /> Read and build quote
        </LoadingButton>
      </div>
    </div>
  )
}
